import { createClient } from '@supabase/supabase-js';
import { toast } from 'sonner';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  },
  global: {
    headers: {
      'x-application-name': 'task-manager'
    }
  },
  db: {
    schema: 'public'
  },
  // Add retry configuration
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// Add error handling for network issues
supabase.auth.onAuthStateChange((event, session) => {
  if (event === 'SIGNED_OUT') {
    // Clear any cached data
    localStorage.removeItem('supabase.auth.token');
  }
});

// Add retry logic for failed requests with exponential backoff
const maxRetries = 3;
const baseDelay = 1000; // Start with 1 second

export const withRetry = async <T>(
  operation: () => Promise<T>,
  retries = maxRetries,
  attempt = 1
): Promise<T> => {
  try {
    return await operation();
  } catch (error) {
    if (retries > 0 && error instanceof Error) {
      // Network or connection errors
      if (error.message.includes('Failed to fetch') || 
          error.message.includes('NetworkError') ||
          error.message.includes('network timeout')) {
        const delay = baseDelay * Math.pow(2, attempt - 1); // Exponential backoff
        console.log(`Retry attempt ${attempt} after ${delay}ms`);
        
        // Show toast for retrying
        toast.loading(`Retrying... Attempt ${attempt} of ${maxRetries}`);
        
        await new Promise(resolve => setTimeout(resolve, delay));
        return withRetry(operation, retries - 1, attempt + 1);
      }
      
      // Database timeout errors
      if (error.message.includes('timeout') || error.message.includes('deadlock')) {
        toast.error('Database timeout. Retrying...');
        await new Promise(resolve => setTimeout(resolve, baseDelay));
        return withRetry(operation, retries - 1, attempt + 1);
      }
    }
    
    throw error;
  }
};

// Add request timeout
export const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number = 10000
): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Request timed out'));
      }, timeoutMs);
    })
  ]);
};